import { ReactNode, useState, useCallback, useRef, useMemo } from 'react';
import {
  AvailableUserThemes,
  BackgroundThemeRevision,
  GroupThemeRevision,
  HeaderThemeRevision,
  NavbarThemeRevision,
  PageRevision,
  PageRevisionResult,
  PageThemeRevision,
  ReferralThemeRevision,
} from 'referral-types';
import useSWR from 'swr';
import { pageApi, themeApi } from '../../api/service';
import { baseEndpoint as pageCacheKey } from '../../api/PageApi';
import { baseEndpoint as themeCacheKey } from '../../api/ThemeApi';
import { useParams } from 'react-router-dom';
import type { FormFields } from '../../components/page/page.schema';
import { updatePageMutation, updatePageOptions } from '../../components/page/mutators';
import { getCleanedObject, LogoStyle, UpdateOptions } from '../../constants';
import { Color } from '../theme/ThemeConstants';
import { useAuth } from '../auth/useAuth';
import { usePage } from '../page/usePage';
import { PageRevisionContext } from './PageRevisionContext';
import { PageRevisionUpdatesContext } from './PageRevisionUpdatesContext';
import { GroupThemeRevisionContext } from './GroupThemeRevisionContext';
import { ReferralThemeRevisionContext } from './ReferralThemeRevisionContext';
import { ThemeProvider } from '../theme/ThemeProvider';
import { useSiteFonts } from '../../hooks/useSiteFonts';

export const PageRevisionProvider = (props: Props) => {
  const { children } = props;
  const { page, isPageEditing, setIsSavingPageChanges } = usePage();
  const { currentUser } = useAuth();
  const { pageId, pageName } = useParams<{ pageId: string; pageName: string }>();

  // Use a ref to store the latest updates without causing re-renders in callbacks
  const updatesRef = useRef<FormFields>({
    backgroundThemeRevision: {},
    pageThemeRevision: {},
    groupThemeRevision: {},
    referralThemeRevision: {},
    navbarThemeRevision: {},
    headerThemeRevision: {},
  });

  const [updates, setUpdatesState] = useState<FormFields>({
    backgroundThemeRevision: {},
    pageThemeRevision: {},
    groupThemeRevision: {},
    referralThemeRevision: {},
    navbarThemeRevision: {},
    headerThemeRevision: {},
  });

  // Wrapper that updates both state and ref
  const setUpdates = useCallback((newUpdates: FormFields | ((prev: FormFields) => FormFields)) => {
    if (typeof newUpdates === 'function') {
      setUpdatesState((prev) => {
        const updated = newUpdates(prev);
        updatesRef.current = updated;
        return updated;
      });
    } else {
      updatesRef.current = newUpdates;
      setUpdatesState(newUpdates);
    }
  }, []);

  const { data: pageRevisions, mutate } = useSWR<PageRevisionResult>(
    [pageCacheKey, pageName != null ? pageName : pageId, '/revisions'],
    () => (pageName != null ? pageApi.getPageRevisionsByName(pageName) : pageApi.getPageRevisions(pageId!)),
    { suspense: true }
  );

  const { data: availableUserThemes } = useSWR<AvailableUserThemes>(
    currentUser ? [themeCacheKey, currentUser?.id, currentUser.roles] : null,
    () => themeApi.getThemesForUser(),
    { suspense: false }
  );

  const [logoOverride, setLogoOverride] = useState<File | undefined | null>();
  const [backgroundImageOverride, setBackgroundImageOverride] = useState<File | undefined | null>();
  const [backgroundImageMobileOverride, setBackgroundImageMobileOverride] = useState<File | undefined | null>();
  const [groupMediaOverride, setGroupMediaOverride] = useState<File | undefined | null>();
  const [headerMediaOverride, setHeaderMediaOverride] = useState<File | undefined | null>();

  const updatePageRevision = useCallback(
    async (options: UpdateOptions = {}) => {
      try {
        setIsSavingPageChanges(true);
        await options.onBefore?.();

        // Use the ref value to avoid dependency on updates state
        const currentUpdates = updatesRef.current;

        await mutate(
          async () =>
            updatePageMutation(page!.id, currentUpdates, pageRevisions!, {
              logoFile: logoOverride,
              backgroundFile: backgroundImageOverride,
              backgroundMobileFile: backgroundImageMobileOverride,
              groupMediaFile: groupMediaOverride,
              headerMediaFile: headerMediaOverride,
            }),
          updatePageOptions(currentUpdates, pageRevisions!)
        );
        setUpdates({
          backgroundThemeRevision: {},
          pageThemeRevision: {},
          groupThemeRevision: {},
          referralThemeRevision: {},
          navbarThemeRevision: {},
          headerThemeRevision: {},
        });
        setLogoOverride(undefined);
        setBackgroundImageOverride(undefined);
        setBackgroundImageMobileOverride(undefined);
        setGroupMediaOverride(undefined);
        setHeaderMediaOverride(undefined);
        await options.onSuccess?.();
      } catch (error) {
        await options.onError?.(error);
        console.error({ error });
      } finally {
        setIsSavingPageChanges(false);
      }
    },
    [
      setIsSavingPageChanges,
      mutate,
      pageRevisions,
      page,
      logoOverride,
      backgroundImageOverride,
      backgroundImageMobileOverride,
      groupMediaOverride,
      headerMediaOverride,
      setUpdates,
    ]
  );
  // Memoize individual theme revisions separately to prevent unnecessary re-renders
  const backgroundThemeRevision = useMemo(() => {
    const base = isPageEditing
      ? pageRevisions?.draftRevision?.backgroundThemeRevision
      : pageRevisions?.revision?.backgroundThemeRevision;
    if (!base) return undefined;
    return isPageEditing ? ({ ...base, ...updates.backgroundThemeRevision } as BackgroundThemeRevision) : base;
  }, [
    isPageEditing,
    pageRevisions?.draftRevision?.backgroundThemeRevision,
    pageRevisions?.revision?.backgroundThemeRevision,
    updates.backgroundThemeRevision,
  ]);

  const pageThemeRevision = useMemo(() => {
    const base = isPageEditing
      ? pageRevisions?.draftRevision?.pageThemeRevision
      : pageRevisions?.revision?.pageThemeRevision;
    if (!base) return undefined;
    return isPageEditing ? ({ ...base, ...updates.pageThemeRevision } as PageThemeRevision) : base;
  }, [
    isPageEditing,
    pageRevisions?.draftRevision?.pageThemeRevision,
    pageRevisions?.revision?.pageThemeRevision,
    updates.pageThemeRevision,
  ]);

  const groupThemeRevision = useMemo(() => {
    const base = isPageEditing
      ? pageRevisions?.draftRevision?.groupThemeRevision
      : pageRevisions?.revision?.groupThemeRevision;
    if (!base) return undefined;
    return isPageEditing ? ({ ...base, ...updates.groupThemeRevision } as GroupThemeRevision) : base;
  }, [
    isPageEditing,
    pageRevisions?.draftRevision?.groupThemeRevision,
    pageRevisions?.revision?.groupThemeRevision,
    updates.groupThemeRevision,
  ]);

  const referralThemeRevision = useMemo(() => {
    const base = isPageEditing
      ? pageRevisions?.draftRevision?.referralThemeRevision
      : pageRevisions?.revision?.referralThemeRevision;
    if (!base) return undefined;
    return isPageEditing ? ({ ...base, ...updates.referralThemeRevision } as ReferralThemeRevision) : base;
  }, [
    isPageEditing,
    pageRevisions?.draftRevision?.referralThemeRevision,
    pageRevisions?.revision?.referralThemeRevision,
    updates.referralThemeRevision,
  ]);

  const navbarThemeRevision = useMemo(() => {
    const base = isPageEditing
      ? pageRevisions?.draftRevision?.navbarThemeRevision
      : pageRevisions?.revision?.navbarThemeRevision;
    if (!base) return undefined;
    return isPageEditing ? ({ ...base, ...updates.navbarThemeRevision } as NavbarThemeRevision) : base;
  }, [
    isPageEditing,
    pageRevisions?.draftRevision?.navbarThemeRevision,
    pageRevisions?.revision?.navbarThemeRevision,
    updates.navbarThemeRevision,
  ]);

  const headerThemeRevision = useMemo(() => {
    const base = isPageEditing
      ? pageRevisions?.draftRevision?.headerThemeRevision
      : pageRevisions?.revision?.headerThemeRevision;
    if (!base) return undefined;
    return isPageEditing ? ({ ...base, ...updates.headerThemeRevision } as HeaderThemeRevision) : base;
  }, [
    isPageEditing,
    pageRevisions?.draftRevision?.headerThemeRevision,
    pageRevisions?.revision?.headerThemeRevision,
    updates.headerThemeRevision,
  ]);
  const pageRevision = useMemo(() => {
    if (!isPageEditing) return pageRevisions?.revision;

    return {
      ...pageRevisions?.draftRevision,
      backgroundThemeRevision,
      pageThemeRevision,
      groupThemeRevision,
      referralThemeRevision,
      navbarThemeRevision,
      headerThemeRevision,
    } as PageRevision;
  }, [
    isPageEditing,
    pageRevisions?.draftRevision,
    pageRevisions?.revision,
    backgroundThemeRevision,
    pageThemeRevision,
    groupThemeRevision,
    referralThemeRevision,
    navbarThemeRevision,
    headerThemeRevision,
  ]);

  const pageTheme = useMemo(
    () => availableUserThemes?.pageThemes.find((o) => o.id === pageThemeRevision?.themeId),
    [availableUserThemes?.pageThemes, pageThemeRevision?.themeId]
  );

  useSiteFonts(pageThemeRevision?.font);

  const updateBackgroundTheme = useCallback(
    (backgroundThemeId: string) => {
      const backgroundTheme = availableUserThemes!.backgroundThemes.find((o) => o.id === backgroundThemeId)!;
      const isReverting = pageRevisions?.draftRevision?.backgroundThemeRevision.themeId === backgroundTheme.id;

      const opacityBlur = isReverting ? undefined : backgroundTheme.defaultOpacityBlur;
      const intensity = isReverting ? undefined : backgroundTheme.defaultIntensity;
      const scale = isReverting ? undefined : backgroundTheme.defaultScale;
      const subTheme = isReverting ? undefined : backgroundTheme.defaultSubTheme;

      const colorId1 = isReverting ? undefined : backgroundTheme.defaultColorId1;
      const colorId2 = isReverting ? undefined : backgroundTheme.defaultColorId2;
      const colorId3 = isReverting ? undefined : backgroundTheme.defaultColorId3;

      // New background media fields
      const media = isReverting ? undefined : backgroundTheme.defaultMedia;
      const mediaRepeat = isReverting ? undefined : backgroundTheme.defaultMediaRepeat;
      const mediaSize = isReverting ? undefined : backgroundTheme.defaultMediaSize;
      const mediaPosition = isReverting ? undefined : backgroundTheme.defaultMediaPosition;
      const mediaAttachment = isReverting ? undefined : backgroundTheme.defaultMediaAttachment;

      const mediaMobile = isReverting ? undefined : backgroundTheme.defaultMediaMobile;
      const mediaRepeatMobile = isReverting ? undefined : backgroundTheme.defaultMediaRepeatMobile;
      const mediaSizeMobile = isReverting ? undefined : backgroundTheme.defaultMediaSizeMobile;
      const mediaPositionMobile = isReverting ? undefined : backgroundTheme.defaultMediaPositionMobile;
      const mediaAttachmentMobile = isReverting ? undefined : backgroundTheme.defaultMediaAttachmentMobile;

      const backgroundThemeRevision = getCleanedObject({
        themeId: isReverting ? undefined : backgroundTheme.id,
        opacityBlur,
        intensity,
        scale,
        subTheme,
        colorId1,
        colorId2,
        colorId3,
        media,
        mediaRepeat,
        mediaSize,
        mediaPosition,
        mediaAttachment,
        mediaMobile,
        mediaRepeatMobile,
        mediaSizeMobile,
        mediaPositionMobile,
        mediaAttachmentMobile,
      });

      setUpdates((prev) => ({
        ...prev,
        backgroundThemeRevision,
      }));
    },
    [availableUserThemes, pageRevisions?.draftRevision?.backgroundThemeRevision.themeId, setUpdates]
  );

  const updatePageTheme = useCallback(
    (pageThemeId: string) => {
      const pageTheme = availableUserThemes!.pageThemes.find((o) => o.id === pageThemeId)!;
      const isReverting = pageRevisions?.draftRevision?.pageThemeRevision.themeId === pageTheme.id;

      const lightMode = isReverting ? undefined : pageTheme.defaultLightMode;
      const font = isReverting ? undefined : pageTheme.defaultFont;
      const primaryColorHex = isReverting ? undefined : pageTheme.defaultPrimaryColorHex;
      const secondaryColorHex = isReverting ? undefined : pageTheme.defaultSecondaryColorHex;
      const tertiaryColorHex = isReverting ? undefined : pageTheme.defaultTertiaryColorHex;
      const pageSize = isReverting ? undefined : pageTheme.defaultPageSize;
      const pageLayout = isReverting ? undefined : pageTheme.defaultPageLayout;

      const pageThemeRevision = getCleanedObject({
        themeId: isReverting ? undefined : pageTheme.id,
        lightMode,
        font,
        primaryColorHex,
        secondaryColorHex,
        tertiaryColorHex,
        pageSize,
        pageLayout,
      });

      setUpdates((prev) => ({
        ...prev,
        pageThemeRevision,
      }));
    },
    [availableUserThemes, pageRevisions?.draftRevision?.pageThemeRevision.themeId, setUpdates]
  );

  const updateGroupTheme = useCallback(
    (groupThemeId: string) => {
      const groupTheme = availableUserThemes!.groupThemes.find((o) => o.id === groupThemeId)!;
      const isReverting = pageRevisions?.draftRevision?.groupThemeRevision.themeId === groupTheme.id;

      const backgroundColorId = isReverting ? undefined : groupTheme.defaultBackgroundColorId;
      const borderColorId = isReverting ? undefined : groupTheme.defaultBorderColorId;
      const titleColorId = isReverting ? undefined : groupTheme.defaultTitleColorId;
      const titleSize = isReverting ? undefined : groupTheme.defaultTitleSize;
      const titleWeight = isReverting ? undefined : groupTheme.defaultTitleWeight;
      const divider = isReverting ? undefined : groupTheme.defaultDivider;
      const border = isReverting ? undefined : groupTheme.defaultBorder;
      const cardCornerStyle = isReverting ? undefined : groupTheme.defaultCardCornerStyle;
      const intensity = isReverting ? undefined : groupTheme.defaultIntensity;
      const opacity = isReverting ? undefined : groupTheme.defaultOpacity;
      const opacityBlur = isReverting ? undefined : groupTheme.defaultOpacityBlur;
      const subTheme = isReverting ? undefined : groupTheme.defaultSubTheme;
      const media = isReverting ? undefined : groupTheme.defaultMedia;
      const mediaSize = isReverting ? undefined : groupTheme.defaultMediaSize;
      const mediaRepeat = isReverting ? undefined : groupTheme.defaultMediaRepeat;
      const mediaPosition = isReverting ? undefined : groupTheme.defaultMediaPosition;
      const mediaAttachment = isReverting ? undefined : groupTheme.defaultMediaAttachment;

      const groupThemeRevision = getCleanedObject({
        themeId: isReverting ? undefined : groupTheme.id,
        backgroundColorId,
        borderColorId,
        titleColorId,
        titleSize,
        titleWeight,
        divider,
        border,
        cardCornerStyle,
        subTheme,
        intensity,
        opacity,
        opacityBlur,
        media,
        mediaSize,
        mediaRepeat,
        mediaPosition,
        mediaAttachment,
      });

      setUpdates((prev) => ({
        ...prev,
        groupThemeRevision,
      }));
    },
    [availableUserThemes, pageRevisions?.draftRevision?.groupThemeRevision.themeId, setUpdates]
  );

  const updateReferralTheme = useCallback(
    (referralThemeId: string) => {
      const referralTheme = availableUserThemes!.referralThemes.find((o) => o.id === referralThemeId)!;
      const isReverting = pageRevisions?.draftRevision?.referralThemeRevision.themeId === referralTheme.id;

      const backgroundColorId = isReverting ? undefined : referralTheme.defaultBackgroundColorId;
      const borderColorId = isReverting ? undefined : referralTheme.defaultBorderColorId;
      const textColorId = isReverting ? undefined : referralTheme.defaultTextColorId;
      const titleSize = isReverting ? undefined : referralTheme.defaultTitleSize;
      const titleWeight = isReverting ? undefined : referralTheme.defaultTitleWeight;
      const titleClamp = isReverting ? undefined : referralTheme.defaultTitleClamp;
      const bodyClamp = isReverting ? undefined : referralTheme.defaultBodyClamp;
      const expandableBody = isReverting ? undefined : referralTheme.defaultExpandableBody;
      const titleClampOptions = isReverting ? undefined : referralTheme.titleClampOptions;
      const defaultTitleClamp = isReverting ? undefined : referralTheme.defaultTitleClamp;
      const bodyClampOptions = isReverting ? undefined : referralTheme.bodyClampOptions;
      const defaultBodyClamp = isReverting ? undefined : referralTheme.defaultBodyClamp;
      const expandableBodyOptions = isReverting ? undefined : referralTheme.expandableBodyOptions;
      const defaultExpandableBody = isReverting ? undefined : referralTheme.defaultExpandableBody;
      const border = isReverting ? undefined : referralTheme.defaultBorder;
      const cardCornerStyle = isReverting ? undefined : referralTheme.defaultCardCornerStyle;
      const initialDisplay = isReverting ? undefined : referralTheme.defaultInitialDisplay;
      const layout = isReverting ? undefined : referralTheme.defaultLayout;

      const imageSize = isReverting ? undefined : referralTheme.defaultImageSize;
      const imageCornerStyle = isReverting ? undefined : referralTheme.defaultImageCornerStyle;
      const imageRatio = isReverting ? undefined : referralTheme.defaultImageRatio;

      const opacity = isReverting ? undefined : referralTheme.defaultOpacity;
      const opacityBlur = isReverting ? undefined : referralTheme.defaultOpacityBlur;

      const subTheme = isReverting ? undefined : referralTheme.defaultSubTheme;

      const referralThemeRevision = getCleanedObject({
        themeId: isReverting ? undefined : referralTheme.id,
        backgroundColorId,
        borderColorId,
        textColorId,
        cardCornerStyle,
        titleSize,
        titleWeight,
        titleClamp,
        bodyClamp,
        expandableBody,
        titleClampOptions,
        defaultTitleClamp,
        bodyClampOptions,
        defaultBodyClamp,
        expandableBodyOptions,
        defaultExpandableBody,
        border,
        initialDisplay,
        layout,
        imageSize,
        imageCornerStyle,
        imageRatio,
        opacity,
        opacityBlur,
        subTheme,
      });

      setUpdates((prev) => ({
        ...prev,
        referralThemeRevision,
      }));
    },
    [availableUserThemes, pageRevisions?.draftRevision?.referralThemeRevision.themeId, setUpdates]
  );

  const updateNavbarTheme = useCallback(
    (navbarThemeId: string) => {
      const navbarTheme = availableUserThemes!.navbarThemes.find((o) => o.id === navbarThemeId)!;
      const isReverting = pageRevisions?.draftRevision?.navbarThemeRevision.themeId === navbarTheme.id;

      const navbarSize = isReverting ? undefined : navbarTheme.defaultNavbarSize;
      const navbarStyle = isReverting ? undefined : navbarTheme.defaultNavbarStyle;
      const logoStyle = isReverting ? undefined : navbarTheme.defaultLogoStyle;
      const subTheme = isReverting ? undefined : navbarTheme.defaultSubTheme;

      const navbarThemeRevision = getCleanedObject({
        themeId: isReverting ? undefined : navbarTheme.id,
        navbarSize,
        navbarStyle,
        logoStyle,
        subTheme,
      });

      setUpdates((prev) => ({
        ...prev,
        navbarThemeRevision,
      }));
    },
    [availableUserThemes, pageRevisions?.draftRevision?.navbarThemeRevision.themeId, setUpdates]
  );

  const updateHeaderTheme = useCallback(
    (headerThemeId: string) => {
      const headerTheme = availableUserThemes!.headerThemes.find((o) => o.id === headerThemeId)!;
      const isReverting = pageRevisions?.draftRevision?.headerThemeRevision.themeId === headerTheme.id;

      const backgroundColorId = isReverting ? undefined : headerTheme.defaultBackgroundColorId;
      const borderColorId = isReverting ? undefined : headerTheme.defaultBorderColorId;
      const textColorId = isReverting ? undefined : headerTheme.defaultTextColorId;
      const socialLinkBackgroundColorId = isReverting ? undefined : headerTheme.defaultSocialLinkBackgroundColorId;
      const socialLinkTextColorId = isReverting ? undefined : headerTheme.defaultSocialLinkTextColorId;

      const titleSize = isReverting ? undefined : headerTheme.defaultTitleSize;
      const titleWeight = isReverting ? undefined : headerTheme.defaultTitleWeight;
      const cardCornerStyle = isReverting ? undefined : headerTheme.defaultCardCornerStyle;
      const border = isReverting ? undefined : headerTheme.defaultBorder;
      const padding = isReverting ? undefined : headerTheme.defaultPadding;
      const layout = isReverting ? undefined : headerTheme.defaultLayout;

      const imageSize = isReverting ? undefined : headerTheme.defaultImageSize;
      const imageCornerStyle = isReverting ? undefined : headerTheme.defaultImageCornerStyle;
      const subTheme = isReverting ? undefined : headerTheme.defaultSubTheme;

      const socialSubTheme = isReverting ? undefined : headerTheme.defaultSocialSubTheme;
      const socialPadding = isReverting ? undefined : headerTheme.defaultSocialPadding;
      const socialCornerStyle = isReverting ? undefined : headerTheme.defaultSocialCornerStyle;
      const socialLabel = isReverting ? undefined : headerTheme.defaultSocialLabel;

      const opacity = isReverting ? undefined : headerTheme.defaultOpacity;
      const opacityBlur = isReverting ? undefined : headerTheme.defaultOpacityBlur;

      const media = isReverting ? undefined : headerTheme.defaultMedia;
      const mediaRepeat = isReverting ? undefined : headerTheme.defaultMediaRepeat;
      const mediaSize = isReverting ? undefined : headerTheme.defaultMediaSize;
      const mediaPosition = isReverting ? undefined : headerTheme.defaultMediaPosition;
      const mediaAttachment = isReverting ? undefined : headerTheme.defaultMediaAttachment;

      const headerThemeRevision = getCleanedObject({
        themeId: isReverting ? undefined : headerTheme.id,
        backgroundColorId,
        borderColorId,
        textColorId,
        socialLinkBackgroundColorId,
        socialLinkTextColorId,
        cardCornerStyle,
        titleSize,
        titleWeight,
        border,
        padding,
        layout,
        imageSize,
        imageCornerStyle,
        opacity,
        opacityBlur,
        subTheme,
        socialSubTheme,
        socialPadding,
        socialCornerStyle,
        socialLabel,
        media,
        mediaRepeat,
        mediaSize,
        mediaPosition,
        mediaAttachment,
      });

      setUpdates((prev) => ({
        ...prev,
        headerThemeRevision,
      }));
    },
    [availableUserThemes, pageRevisions?.draftRevision?.headerThemeRevision.themeId, setUpdates]
  );

  const refresh = useCallback(() => {
    return mutate(undefined, { revalidate: true });
  }, [mutate]);

  const handleSetLogoOverride = useCallback(
    (file: File | undefined | null) => {
      setLogoOverride(file);

      setUpdatesState((prev) => ({
        ...prev,
        navbarThemeRevision: {
          ...prev.navbarThemeRevision,
          logoStyle: file != null ? LogoStyle.custom : LogoStyle.default,
        },
      }));
    },
    [setUpdatesState]
  );

  const handleSetBackgroundImageOverride = useCallback((file: File | null | undefined) => {
    setBackgroundImageOverride(file);
  }, []);

  const handleSetBackgroundImageMobileOverride = useCallback((file: File | null | undefined) => {
    setBackgroundImageMobileOverride(file);
  }, []);

  const handleSetGroupMediaOverride = useCallback((file: File | null | undefined) => {
    setGroupMediaOverride(file);
  }, []);

  const handleSetHeaderMediaOverride = useCallback((file: File | null | undefined) => {
    setHeaderMediaOverride(file);
  }, []);

  const contextValue = useMemo(
    () => ({
      pageRevisions,
      pageRevision,

      // Individual memoized theme revisions
      backgroundThemeRevision,
      pageThemeRevision,
      groupThemeRevision,
      referralThemeRevision,
      navbarThemeRevision,
      headerThemeRevision,

      availableUserThemes,
      updatePageRevision,
      mutator: mutate,
      refresh,
      updateBackgroundTheme,
      updatePageTheme,
      updateGroupTheme,
      updateReferralTheme,
      updateNavbarTheme,
      updateHeaderTheme,
      setLogoOverride: handleSetLogoOverride,
      logoOverride,
      setBackgroundImageOverride: handleSetBackgroundImageOverride,
      backgroundImageOverride,
      setBackgroundImageMobileOverride: handleSetBackgroundImageMobileOverride,
      backgroundImageMobileOverride,
      handleSetGroupMediaOverride,
      groupMediaOverride,
      handleSetHeaderMediaOverride,
      headerMediaOverride,
    }),
    [
      pageRevisions,
      pageRevision,

      // Individual theme revisions
      backgroundThemeRevision,
      pageThemeRevision,
      groupThemeRevision,
      referralThemeRevision,
      navbarThemeRevision,
      headerThemeRevision,

      availableUserThemes,
      // Note: updates, setUpdates, and hasUnsavedChanges moved to separate PageRevisionUpdatesContext
      updatePageRevision,
      mutate,
      refresh,
      updateBackgroundTheme,
      updatePageTheme,
      updateGroupTheme,
      updateReferralTheme,
      updateNavbarTheme,
      updateHeaderTheme,
      handleSetLogoOverride,
      logoOverride,
      handleSetBackgroundImageOverride,
      backgroundImageOverride,
      handleSetBackgroundImageMobileOverride,
      backgroundImageMobileOverride,
      handleSetGroupMediaOverride,
      groupMediaOverride,
      handleSetHeaderMediaOverride,
      headerMediaOverride,
    ]
  );

  // Separate updates context to prevent re-renders of components that don't need it
  // Include hasUnsavedChanges here since it depends on updates
  const updatesContextValue = useMemo(
    () => ({
      updates,
      setUpdates,
      hasUnsavedChanges: Boolean(
        Object.values(updates).some((nestedObj) => Object.keys(nestedObj).length > 0) ||
        logoOverride ||
        logoOverride === null ||
        backgroundImageOverride ||
        backgroundImageOverride === null ||
        backgroundImageMobileOverride ||
        backgroundImageMobileOverride === null ||
        groupMediaOverride ||
        groupMediaOverride === null ||
        headerMediaOverride ||
        headerMediaOverride === null
      ),
    }),
    [updates, setUpdates, logoOverride, backgroundImageOverride, backgroundImageMobileOverride, groupMediaOverride, headerMediaOverride]
  );

  // Fine-grained context values for group and referral theme revisions
  // These prevent cross-section re-renders (e.g., group theme changes won't re-render referral cards)
  const groupThemeRevisionContextValue = useMemo(
    () => ({ groupThemeRevision, groupMediaOverride }),
    [groupThemeRevision, groupMediaOverride]
  );

  const referralThemeRevisionContextValue = useMemo(() => ({ referralThemeRevision }), [referralThemeRevision]);

  return (
    <PageRevisionContext.Provider value={contextValue}>
      <PageRevisionUpdatesContext.Provider value={updatesContextValue}>
        <GroupThemeRevisionContext.Provider value={groupThemeRevisionContextValue}>
          <ReferralThemeRevisionContext.Provider value={referralThemeRevisionContextValue}>
            <ThemeProvider
              color={pageTheme == null ? Color.blue : pageTheme.colorId}
              lightMode={pageThemeRevision?.lightMode}
            >
              {children}
            </ThemeProvider>
          </ReferralThemeRevisionContext.Provider>
        </GroupThemeRevisionContext.Provider>
      </PageRevisionUpdatesContext.Provider>
    </PageRevisionContext.Provider>
  );
};

type Props = {
  children: ReactNode;
};
